import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnResizer } from './ColumnResizer';
import { MAX_PANEL_WIDTH, MIN_PANEL_WIDTH } from './column-width';

/**
 * The parts of the page the editors sit in but do not own: the width of the
 * controls column, and the two slots the page fills around them. Grouped so an
 * editor forwards one prop rather than four it has nothing to do with.
 */
export interface ScanEditorChrome {
  /** Width of the controls column, which the page's grid also needs. */
  panelWidth: number;
  onPanelWidthChange: (width: number) => void;
  /** Above the photo: how the analysis of this scan is getting on. */
  notice?: ReactNode;
  /**
   * At the head of the controls column: what is done *with* the climb once it
   * has been described — saving it, looking at it again, throwing it away.
   *
   * At the top because it is the column's answer to "and then what", and
   * because a form as long as this one would otherwise hide its own save button
   * below the fold of a narrow panel.
   */
  actions?: ReactNode;
  /**
   * Above everything in the controls column: the circuit, in the order it is
   * climbed, for a climb that is one.
   *
   * At the very top because it is read against the photo beside it — the list
   * says which hold comes next and the picture says where it is — and a setter
   * reordering a circuit is looking from one to the other. Capped at half the
   * column and scrolling inside that, so a long circuit never pushes the save out
   * of reach.
   */
  circuit?: ReactNode;
  /** Under the controls: the scan's own settings. */
  tools?: ReactNode;
  /**
   * What the two columns are called, for a screen reader. The crag panel draws
   * routes on a sector's photo with the same editors, and "Scan photo" is the
   * wrong thing to hear there — so the page that owns the grid names them.
   */
  imageLabel?: string;
  panelLabel?: string;
}

interface ScanEditorLayoutProps extends ScanEditorChrome {
  /** The photo and whatever is drawn on it. */
  canvas: ReactNode;
  /** The controls that act on it — absent while there is nothing to draw. */
  children?: ReactNode;
}

/**
 * The photo column and the controls column, with the divider between them.
 *
 * It renders a **fragment, not a wrapper**: these are two columns of the page's
 * grid, so they have to be its children to be laid out and to scroll
 * independently. Boxing them in a div of their own would make them one cell
 * again, which is what they stopped being.
 *
 * The photo is the elastic column and the controls are the width that was
 * dragged, so narrowing them gives the room to the picture.
 */
export function ScanEditorLayout({
  canvas,
  children,
  panelWidth,
  onPanelWidthChange,
  notice,
  actions,
  circuit,
  tools,
  imageLabel,
  panelLabel,
}: ScanEditorLayoutProps) {
  const { t } = useTranslation();

  return (
    <>
      <section
        className="min-h-0 space-y-3 overflow-y-auto rounded-md border p-3"
        aria-label={imageLabel ?? t('gymAdmin.scans.imageLabel')}
        data-testid="scan-canvas-column"
      >
        {notice}
        {canvas}
      </section>

      <ColumnResizer
        width={panelWidth}
        onWidthChange={onPanelWidthChange}
        min={MIN_PANEL_WIDTH}
        max={MAX_PANEL_WIDTH}
        label={t('gymAdmin.scans.resizePanel')}
        side="right"
      />

      <aside
        className="min-h-0 space-y-3 overflow-y-auto rounded-md border p-3 lg:w-[var(--panel-width)]"
        aria-label={panelLabel ?? t('gymAdmin.scans.panelLabel')}
        data-testid="scan-editor-panel"
      >
        {circuit && (
          <div
            className="max-h-[50%] overflow-y-auto border-b pb-3"
            data-testid="scan-circuit-column"
          >
            {circuit}
          </div>
        )}
        {actions}
        {children}
        {tools}
      </aside>
    </>
  );
}
